import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import { filter } from 'rxjs/operators';
import { StaffType } from '../../enums/StaffType';
import Membership from '../../models/Membership.model';
import { AuthService } from '../../services/auth/auth.service';
import { PBService } from '../../services/pocketbase.service';
import { SettingsService } from '../../services/settings.service';
import { ToastContainerComponent } from '../shared/toast/toast-container.component';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatDividerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    ToastContainerComponent,
  ],
  templateUrl: './sidenav.component.html',
})
export class SidenavComponent implements OnInit {
  public authService = inject(AuthService);
  private breakpointObserver = inject(BreakpointObserver);
  public router = inject(Router);
  public translateSvc = inject(TranslateService);
  public settingsService = inject(SettingsService);
  public pb = inject(PBService);

  sidenav = viewChild.required(MatSidenav);
  isHandset$: Observable<boolean>;
  isVenueContextReady = signal(false);
  isCustomerContextReady = this.isVenueContextReady;

  languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ];

  constructor() {
    // Inject services first
    this.router = inject(Router);
    this.authService = inject(AuthService);
    this.breakpointObserver = inject(BreakpointObserver);
    this.translateSvc = inject(TranslateService);
    this.settingsService = inject(SettingsService);

    // Now that breakpointObserver is initialized, you can define isHandset$
    this.isHandset$ = this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(map((result) => result.matches));

    const availableLangs = this.languages.map((l) => l.code);
    this.translateSvc.addLangs(this.languages.map((l) => l.code));
    this.translateSvc.setDefaultLang('en');

    const saved = localStorage.getItem('userLang');
    const browser = this.translateSvc.getBrowserLang() || 'en';
    const initial =
      saved ?? (availableLangs.includes(browser) ? browser : 'en');

    this.translateSvc.use(initial);
  }

  currentRoute: string = '';

  sections: {
    key: string;
    title: string;
    icon: string;
    iconBg: string;
    iconColor: string;
    borderColor: string;
    badgeColor: string;
    type: 'simple' | 'grouped';
    expanded: boolean;
    items?: any[];
    groups?: any[];
  }[] = [];

  accountingTitle: string = '';

  get userInitial(): string {
    const user = this.pb.$user();
    const email = user?.['email'] || '';
    const name = user?.['name'] || '';
    return (name || email || 'U').charAt(0).toUpperCase();
  }

  get userDisplayName(): string {
    const user = this.pb.$user();
    return user?.['name'] || user?.['email'] || 'User';
  }

  get userSubTitle(): string {
    const user = this.pb.$user();
    const name = user?.['name'];
    const email = user?.['email'];
    if (name && name !== email) {
      return email;
    }
    const membership = this.settingsService.selectedMembership();
    if (membership?.Role !== undefined) {
      return this.getRoleName(membership.Role);
    }
    return '';
  }

  getRoleName(role: number | undefined | null): string {
    if (role === undefined || role === null) return '';
    return StaffType.getName(role as StaffType);
  }

  async ngOnInit() {
    this.initNavItems();
    this.setInitialState();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setInitialState();

        this.isHandset$.subscribe((isHandset) => {
          if (isHandset) {
            this.sidenav().close();
          }
        });
      });

    await this.settingsService.loadMemberships();
    this.isVenueContextReady.set(true);

    const accountingRoute = this.router.config.find(
      (r) => r.path === 'accounting'
    );
    this.accountingTitle =
      accountingRoute?.data?.['title'] ??
      accountingRoute?.title ??
      this.formatTitle('accounting');

    // Update accounting title in sections if initialized
    const accSection = this.sections.find(s => s.key === 'accounting');
    if (accSection) accSection.title = this.accountingTitle;
  }

  private initNavItems() {
    this.sections = [];

    // Menu Management
    this.sections.push({
      key: 'menu',
      title: 'NAV.MENU_MANAGEMENT.TITLE',
      icon: 'restaurant_menu',
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-100',
      badgeColor: 'bg-orange-500',
      type: 'simple',
      expanded: false,
      items: this.getNavItems('menu-management')
    });

    // Inventory Management
    this.sections.push({
      key: 'inventory',
      title: 'NAV.INVENTORY_MANAGEMENT.TITLE',
      icon: 'inventory_2',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      borderColor: 'border-emerald-100',
      badgeColor: 'bg-emerald-500',
      type: 'simple',
      expanded: false,
      items: this.getNavItems('inventory-management')
    });

    // Store Management (Grouped)
    const storeCfg = this.router.config.find(
      (r) => r.path === 'store-management'
    );
    const storeGroups =
      storeCfg?.children?.map((group) => ({
        path: group.path!,
        title:
          group.data?.['title'] ?? group.title ?? this.formatTitle(group.path!),
        icon: this.getIconForRoute(group.path!),
        expanded: false,
        children:
          group.children?.map((child) => ({
            path: `/store-management/${group.path}/${child.path}`,
            title:
              child.data?.['title'] ??
              child.title ??
              this.formatTitle(child.path!),
            icon: this.getIconForRoute(child.path!),
          })) ?? [],
      })) ?? [];

    this.sections.push({
      key: 'store',
      title: 'NAV.STORE_MANAGEMENT.TITLE',
      icon: 'storefront',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-100',
      badgeColor: 'bg-blue-500',
      type: 'grouped',
      expanded: false,
      groups: storeGroups
    });

    // Reports
    this.sections.push({
      key: 'reports',
      title: 'NAV.REPORTS.TITLE',
      icon: 'analytics',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-100',
      badgeColor: 'bg-purple-500',
      type: 'simple',
      expanded: false,
      items: this.getNavItems('reports')
    });

    // Accounting
    this.sections.push({
      key: 'accounting',
      title: this.accountingTitle || 'Accounting',
      icon: 'account_balance',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      borderColor: 'border-amber-100',
      badgeColor: 'bg-amber-500',
      type: 'simple',
      expanded: false,
      items: this.getNavItems('accounting')
    });

    // Settings
    this.sections.push({
      key: 'settings',
      title: 'NAV.SETTINGS.TITLE',
      icon: 'settings',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-700',
      borderColor: 'border-slate-200',
      badgeColor: 'bg-slate-600',
      type: 'simple',
      expanded: false,
      items: this.getNavItems('settings')
    });
  }

  private setInitialState() {
    this.currentRoute = this.router.url;
    const routeParts = this.currentRoute.split('/').filter((p) => p);

    this.sections.forEach((s) => (s.expanded = false));

    // Collapse all store groups
    const storeSection = this.sections.find((s) => s.key === 'store');
    if (storeSection && storeSection.groups) {
      storeSection.groups.forEach((g: any) => (g.expanded = false));
    }

    // Collapse all sub-groups in simple sections
    this.sections.forEach((s) => {
      if (s.items) {
        s.items.forEach((it: any) => {
          if (it.isGroup) it.expanded = false;
        });
      }
    });

    const mainSection = routeParts[0];
    const activeSection = this.sections.find((s) => {
      // Mapping route part to section key
      if (s.key === 'menu' && mainSection === 'menu-management') return true;
      if (s.key === 'inventory' && mainSection === 'inventory-management') return true;
      if (s.key === 'store' && mainSection === 'store-management') return true;
      return s.key === mainSection;
    });

    if (activeSection) {
      activeSection.expanded = true;

      if (activeSection.key === 'store') {
        const groupPath = routeParts[1];
        const activeGroup = activeSection.groups?.find((g: any) => g.path === groupPath);
        if (activeGroup) activeGroup.expanded = true;
      }

      if (activeSection.items) {
        const groupPath = routeParts[1];
        const activeItem = activeSection.items.find(
          (it: any) => it.isGroup && it.path === groupPath
        );
        if (activeItem) activeItem.expanded = true;
      }
    }
  }

  private getNavItems(parentPath: string) {
    const parentRoute = this.router.config.find((r) => r.path === parentPath);
    return (
      parentRoute?.children
        ?.filter((child) => child.path && child.path !== '' && !child.redirectTo)
        .map((child) => {
          const validChildren = child.children?.filter(
            (c) => c.path && c.path !== '' && !c.redirectTo
          );
          if (validChildren && validChildren.length > 0) {
            return {
              isGroup: true,
              path: child.path!,
              title:
                child.data?.['title'] ??
                child.title ??
                this.formatTitle(child.path!),
              icon: child.data?.['icon'] ?? this.getIconForRoute(child.path!),
              expanded: false,
              children: validChildren.map((c) => ({
                path: `/${parentPath}/${child.path}/${c.path}`,
                title:
                  c.data?.['title'] ??
                  c.title ??
                  this.formatTitle(c.path!),
                icon: c.data?.['icon'] ?? this.getIconForRoute(c.path!),
              })),
            };
          }
          return {
            isGroup: false,
            path: `/${parentPath}/${child.path}`,
            title:
              child.data?.['title'] ??
              child.title ??
              this.formatTitle(child.path!),
            icon: child.data?.['icon'] ?? this.getIconForRoute(child.path!),
          };
        }) ?? []
    );
  }

  private formatTitle(path: string): string {
    return path.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private getIconForRoute(path: string): string {
    const icons: Record<string, string> = {
      menus: 'restaurant_menu',
      categories: 'category',
      products: 'fastfood',
      inventory: 'inventory_2',
      'inventory-movements': 'trending_up',
      'option-groups': 'tune',
      'master-catalog': 'menu_book',
      'master-products': 'inventory',
      'master-categories': 'category',
      definitions: 'folder_open',
      warehouses: 'warehouse',
      'inventory-batches': 'layers',
      waybills: 'local_shipping',
      invoices: 'receipt_long',
      'inventory-groups': 'layers',
      users: 'people',
      layout: 'layers',
      configuration: 'settings',
      staff: 'badge',
      floors: 'layers',
      tables: 'table_restaurant',
      devices: 'devices',
      printers: 'print',
      points: 'location_on',
      'end-of-day': 'today',
      register: 'point_of_sale',
      sales: 'attach_money',
      general: 'tune',
      'app-settings': 'apps',
      subscription: 'subscriptions',
      accounting: 'account_balance',
      'payment-provider': 'credit_card',
      accounts: 'account_balance_wallet',
      transactions: 'receipt_long',
      receivables: 'arrow_circle_down',
      payables: 'arrow_circle_up',
      summary: 'summarize',
      sessions: 'history',
      production: 'precision_manufacturing',
      tools: 'qr_code_2',
    };
    return icons[path] || 'dashboard';
  }

  isSectionActive(section: any): boolean {
    if (section.type === 'simple' && section.items) {
      return section.items.some((item: any) => {
        if (item.isGroup && item.children) {
          return item.children.some((child: any) => this.currentRoute.startsWith(child.path));
        }
        return this.currentRoute.startsWith(item.path);
      });
    }
    if (section.type === 'grouped' && section.groups) {
      return section.groups.some((group: any) =>
        group.children?.some((child: any) => this.currentRoute.startsWith(child.path))
      );
    }
    return false;
  }

  toggleSection(section: any) {
    this.sections.forEach(s => {
      if (s !== section) s.expanded = false;
    });
    section.expanded = !section.expanded;
  }

  toggleSubGroup(group: any) {
    group.expanded = !group.expanded;
    if (group.expanded) {
      const storeSection = this.sections.find(s => s.key === 'store');
      if (storeSection && storeSection.groups) {
        storeSection.groups.forEach((g: any) => {
          if (g !== group) g.expanded = false;
        });
      }
      this.sections.forEach((s) => {
        if (s.items) {
          s.items.forEach((it: any) => {
            if (it.isGroup && it !== group) it.expanded = false;
          });
        }
      });
    }
  }

  isSubGroupActive(groupPath: string): boolean {
    return this.currentRoute.includes(`/${groupPath}/`) || this.currentRoute.endsWith(`/${groupPath}`);
  }

  logout() {
    this.authService.logout();
    this.settingsService.clear();
  }

  changeLang(code: string) {
    if (!this.languages.find((l) => l.code === code)) return;
    this.translateSvc.use(code);
    localStorage.setItem('userLang', code);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = code;
    }
  }

  changeVenue(membership: Membership) {
    this.settingsService.setCurrentMembership(membership);
  }
  changeCustomer(membership: Membership) {
    this.changeVenue(membership);
  }
}
